import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ConfigProvider, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { store, RootState, AppDispatch } from './store';
import AppLayout from './components/Layout/AppLayout';
import Dashboard from './pages/Dashboard';
import GpuManagement from './pages/GpuManagement';
import TaskMonitoring from './pages/TaskMonitoring';
import HealthCheck from './pages/HealthCheck';
import AlertManagement from './pages/AlertManagement';
import TaskVersionManagement from './pages/TaskVersionManagement';
import TaskMetadataManagement from './pages/TaskMetadataManagement';
import TaskWorkflowConfigManagement from './pages/TaskWorkflowConfigManagement';
import GpuClusterMonitoring from './pages/GpuClusterMonitoring';
import Settings from './pages/Settings';
import Login from './pages/Login';
import RecentProcessedTasks from './pages/RecentProcessedTasks';
import TaskTestTemplate from './pages/TaskTestTemplate';
import { logout } from './store/slices/authSlice';
import { onAuthLogout } from './utils/auth';
import './App.css';

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const themeMode = useSelector((state: RootState) => state.settings.theme);
  const isAuthenticated = useSelector((state: RootState) => Boolean(state.auth.token));

  useEffect(() => {
    const unsubscribe = onAuthLogout(() => {
      dispatch(logout());
    });
    return unsubscribe;
  }, [dispatch]);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      <Router>
        {isAuthenticated ? (
          <AppLayout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/gpu-management" element={<GpuManagement />} />
              <Route path="/task-version-management" element={<TaskVersionManagement />} />
              <Route path="/task-metadata-management" element={<TaskMetadataManagement />} />
              <Route path="/task-workflow-config-management" element={<TaskWorkflowConfigManagement />} />
              <Route path="/gpu-cluster-monitoring" element={<GpuClusterMonitoring />} />
              <Route path="/task-monitoring" element={<TaskMonitoring />} />
              <Route path="/recent-processed-tasks" element={<RecentProcessedTasks />} />
              <Route path="/task-test-template" element={<TaskTestTemplate />} />
              <Route path="/health-check" element={<HealthCheck />} />
              <Route path="/alert-management" element={<AlertManagement />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppLayout>
        ) : (
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
      </Router>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
